import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import {AssignUserToCustomerDataGridCellKeys, CustomersListDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import Button from "../../../../base/button/button";
import SearchInput from "../../../../base/input/search_input";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {formatPhoneNumber} from "../../../../../../core/services/utils/utils";
import {normalize} from "phone-formatter";

const dataGridSaveConfig = {
    name: 'AssignUserToCustomerDialogExistingCustomersSectionDataGrid',
    version: 1,
}

const AssignUserToCustomerDialogExistingCustomersSection = ({submitting, join}) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Searches the customers from the server based on the keywords provided.
     * @type {function(*=, *)}
     */
    const search = useCallback(async (keywords, cancelTokenSource) => {
        setLoading(true);
        const forApi = {
            sorts: [],
            pagination: {currentPage: 1, pageSize: 10},
            keyword: keywords?.trim() ?? '',
        }
        const response = await Api.searchCustomersV2(forApi, cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            setCustomers(response.data?.items ?? []);
        }
    }, [isMounted])

    /**
     * As soon as the component mounts:
     * - preloads the list of customers from the server.
     */
    useEffect(() => {
        void search();
    }, [search])

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Name",
                name: CustomersListDataGridCellKeys.name,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 180,
                pinned: true,
            },
            {
                title: "Code",
                name: CustomersListDataGridCellKeys.code,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Address",
                name: CustomersListDataGridCellKeys.address,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 200,
            },
            {
                title: "City",
                name: CustomersListDataGridCellKeys.city,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Province/State",
                name: CustomersListDataGridCellKeys.province,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 120,
            },
            {
                title: "Postal/Zip Code",
                name: CustomersListDataGridCellKeys.postalCode,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 120,
            },
            {
                title: "Phone",
                name: CustomersListDataGridCellKeys.phone,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                name: AssignUserToCustomerDataGridCellKeys.select,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
                width: 150,
            },
        ], [])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return customers
                ?.map((customer) => {
                    return {
                        key: customer.id,
                        data: customer,
                        cells: {
                            [CustomersListDataGridCellKeys.name]: customer.name,
                            [CustomersListDataGridCellKeys.code]: customer.code,
                            [CustomersListDataGridCellKeys.address]: customer.address,
                            [CustomersListDataGridCellKeys.city]: customer?.city,
                            [CustomersListDataGridCellKeys.province]: customer.province,
                            [CustomersListDataGridCellKeys.postalCode]: customer.postalCode,
                            [CustomersListDataGridCellKeys.phone]: customer.phoneNumber ? formatPhoneNumber(normalize(customer.phoneNumber)) : "",
                            [AssignUserToCustomerDataGridCellKeys.select]:
                                <Button
                                    disabled={submitting}
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={() => join(customer)}
                                >
                                    Accept and Join
                                </Button>,
                        }
                    }
                })
            ?? []
    }, [customers, submitting, join])

    return (
        <>
            <p className={`text-md black font-weight-semi-bold text-left mb-3`}>
                Existing Customers:
            </p>
            <SearchInput
                loading={loading}
                autoSearch
                timeout={1000}
                search={search}
                label={'Search'}
                variant={"outlined"}
                paperProps={{className: 'px-0'}}
            />
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                rows={dataGridRows}
                columns={dataGridColumns}
                hideExporting
                hideFooter
                visibleRows={5}
                state={{
                    loading: {state: loading}
                }}
            />
        </>
    );
}

export default AssignUserToCustomerDialogExistingCustomersSection;
